import {
  ListingCollection,
  ListingCollectionDetail,
  RawListingCollection,
  RawListingCollectionListResponse,
  RawListingCollectionMember,
} from "../../../interfaces/ListingCollectionObject";
import { ProductDetailsModal } from "../../../interfaces/ProductObject";
import { convertRawProduct } from "./productConvertor";

/** Members arrive either as bare ids or as (possibly sparse) product records. */
const memberId = (member: RawListingCollectionMember): string => {
  if (typeof member === "string") return member;
  return (member.productId ?? member.product_id) ?? "";
};

export function convertRawListingCollection(
  raw: RawListingCollection
): ListingCollection {
  return {
    uid: raw.uid,
    creatorId: raw.creatorId ?? "",
    creatorName: raw.creatorName ?? null,
    name: raw.collectionName ?? "Untitled collection",
    productIds: (raw.collection ?? []).map(memberId).filter(Boolean),
    bannerUrl: raw.bannerUrl ?? null,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
  };
}

export function convertRawListingCollectionList(
  raw: RawListingCollectionListResponse
): ListingCollection[] {
  // `contents` is null on a successful empty result — not an error.
  return (raw?.contents ?? []).map(convertRawListingCollection);
}

/**
 * A member is only renderable once it has populated product data. With
 * `includeListingDetails=false` the backend populates only the id and nulls
 * every other field — those stubs must be dropped or they produce
 * `ProductDetailsModal` objects with every field undefined in Redux.
 */
const isRenderableMember = (
  member: RawListingCollectionMember
): member is Record<string, unknown> => {
  if (typeof member === "string") return false;
  const m = member as Record<string, unknown>;
  return Boolean(memberId(member)) && Boolean(m.title ?? m.product_name ?? m.productName);
};

/**
 * The collection endpoint's envelope: `contents` holds a single collection
 * whose `collection` array carries the member products. Fetch with
 * `includeListingDetails=true` — with it false, members come back as
 * id-only stubs, which are dropped here as not worth rendering.
 */
export function convertRawListingCollectionDetail(
  raw: RawListingCollectionListResponse
): ListingCollectionDetail | null {
  const first = (raw?.contents ?? [])[0];
  if (!first) return null;

  const products: ProductDetailsModal[] = (first.collection ?? [])
    .filter(isRenderableMember)
    .map(convertRawProduct);

  return { collection: convertRawListingCollection(first), products };
}

export default {
  convertRawListingCollection,
  convertRawListingCollectionList,
  convertRawListingCollectionDetail,
};
